import { Pencil } from "lucide-react";
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { formatWhatsAppJid, getDisplayName, getContactAvatarUrl } from "@/utils/contactUtils";
import type { Database } from "@/lib/supabase";
import { ScheduleMessageModal } from "./ScheduleMessageModal";
import { FollowUpCountdownBadge } from "./FollowUpCountdownBadge";
import { useContactFollowUpCountdown } from "@/hooks/useContactFollowUpCountdown";

type AccountContact = Database['public']['Tables']['account_contacts']['Row']

interface ChatHeaderProps {
  chatName: string;
  isGroup?: boolean;
  selectedContact: AccountContact | null;
  onContactUpdate?: () => void;
  onContactUpdateOptimistic?: (updates: Partial<AccountContact>) => void;
}

export const ChatHeader = ({ chatName, isGroup, selectedContact, onContactUpdate, onContactUpdateOptimistic }: ChatHeaderProps) => {
  const { user } = useAuth();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [formData, setFormData] = useState({
    display_name: '',
    email: '',
    phone: '',
  });
  const followUpCountdown = useContactFollowUpCountdown({
    contactId: selectedContact?.id,
    assistantIsActive: selectedContact?.assistant_is_active,
  });

  useEffect(() => {
    setImageError(false);
  }, [selectedContact?.id]);

  useEffect(() => {
    if (selectedContact) {
      // Nome: usa getDisplayName que segue a mesma lógica da terceira coluna
      const displayName = getDisplayName(selectedContact);
      
      // Telefone: usa phone se existir, senão formata o jid (igual à terceira coluna)
      const formattedPhone = selectedContact.phone || formatWhatsAppJid(selectedContact.jid);
      
      setFormData({
        display_name: displayName,
        email: selectedContact.email || '',
        phone: formattedPhone,
      });
    }
  }, [selectedContact]);

  const handleSave = async () => {
    if (!selectedContact || !user?.account_id || !supabase) return;

    setLoading(true);
    try {
      const updateData: Partial<AccountContact> = {
        name: formData.display_name || null,
        display_name: formData.display_name || null,
        email: formData.email || null,
        // phone removido - não deve ser editado
        push_name_last: formData.display_name || null,
      };

      // Atualização otimista - atualizar UI imediatamente
      onContactUpdateOptimistic?.(updateData);

      // Salvar no banco de dados - usar apenas o ID do contato da rota
      const { error } = await supabase
        .from('account_contacts')
        .update(updateData)
        .eq('id', selectedContact.id)
        .select()
        .maybeSingle();

      if (error) throw error;

      // Mostrar feedback de sucesso
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);

      // Fechar o dialog após um breve delay para mostrar o sucesso
      setTimeout(() => setIsDialogOpen(false), 300);

      // Chamar onContactUpdate como backup (realtime)
      onContactUpdate?.();
    } catch (err: any) {
      console.error('Erro ao atualizar contato:', err);
      alert('Erro ao atualizar contato: ' + (err.message || 'Erro desconhecido'));

      // Em caso de erro, refetch para garantir consistência
      onContactUpdate?.();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border-b border-border dark:border-[#3F3F464D] px-3 py-2.5 text-foreground dark:text-neutral-50 flex justify-between items-center bg-card dark:bg-transparent">
      <div className="flex gap-2 items-center">
        <div className="w-8 h-8 bg-card-muted dark:bg-neutral-600 rounded-full flex items-center justify-center overflow-hidden">
          {isGroup ? (
            <svg className="w-4 h-4 text-muted-foreground dark:text-neutral-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          ) : selectedContact?.profile_picture && !imageError ? (
            <img
              src={getContactAvatarUrl(selectedContact)}
              alt={chatName}
              className="w-full h-full object-cover"
              onError={() => setImageError(true)}
            />
          ) : (
            <img
              src={selectedContact ? getContactAvatarUrl({ ...selectedContact, profile_picture: null }) : undefined}
              alt={chatName}
              className="w-full h-full object-cover"
            />
          )}
        </div>
        <div>
          <h2 className="text-sm font-semibold">{chatName}</h2>
        </div>
      </div>
      {!isGroup && selectedContact && (
        <div className="flex items-center gap-2">
          {followUpCountdown.visible && (
            <FollowUpCountdownBadge
              label={followUpCountdown.label}
              variant={followUpCountdown.variant}
            />
          )}
          <button
            onClick={() => setIsScheduleModalOpen(true)}
            className="flex items-center border border-border p-2 rounded-[12px] bg-card hover:bg-hover transition-colors duration-200 dark:border-[#3F3F46] dark:bg-neutral-600 dark:hover:bg-neutral-700"
            aria-label="Agendar mensagem"
          >
            <img
              src="/communications.png"
              alt="Agendar mensagem"
              className="w-4 h-4 dark:invert"
            />
          </button>
          <ScheduleMessageModal
            open={isScheduleModalOpen}
            onOpenChange={setIsScheduleModalOpen}
            contact={selectedContact}
          />
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <button className="flex items-center border border-border p-2 rounded-[12px] bg-card hover:bg-hover transition-colors duration-200 dark:border-[#3F3F46] dark:bg-neutral-600 dark:hover:bg-neutral-700">
              <Pencil className="w-4 h-4 text-muted-foreground dark:text-neutral-50" />
            </button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border dark:bg-neutral-800 dark:border-neutral-700">
            <DialogHeader>
              <DialogTitle className="text-foreground dark:text-neutral-50">Editar Contato</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col gap-4 py-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="edit-name" className="text-foreground dark:text-neutral-50">Nome</Label>
                <Input
                  id="edit-name"
                  value={formData.display_name}
                  onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                  className="bg-input border-border text-foreground dark:bg-neutral-700 dark:border-neutral-600 dark:text-neutral-50"
                  placeholder="Nome do contato"
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="edit-email" className="text-foreground dark:text-neutral-50">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-input border-border text-foreground dark:bg-neutral-700 dark:border-neutral-600 dark:text-neutral-50"
                  placeholder="email@exemplo.com"
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="edit-phone" className="text-foreground dark:text-neutral-50">Telefone</Label>
                <Input
                  id="edit-phone"
                  value={formData.phone}
                  disabled={true}
                  className="bg-input border-border text-foreground disabled:opacity-50 disabled:cursor-not-allowed dark:bg-neutral-700 dark:border-neutral-600 dark:text-neutral-50"
                  placeholder="+55 (11) 99999-9999"
                />
              </div>
              <Button
                onClick={handleSave}
                disabled={loading}
                className="w-full mt-2"
              >
                {loading ? 'Salvando...' : 'Salvar'}
              </Button>
              {showSuccess && (
                <div className="text-xs text-green-400 bg-green-500/10 p-2 rounded-lg flex items-center gap-2 border border-green-500/30 animate-in fade-in slide-in-from-top-2 duration-300">
                  <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="font-medium">Contato atualizado!</span>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
        </div>
      )}
    </div>
  );
};
